// Live assist, the sales manager's floating panel. The assist joins the room hidden and listen-only (agent/), and
// sends what it makes of the conversation to the sales manager's identity alone, as text streams on one topic: the
// customer's device is never sent a byte of it, and never learns the assist is in the room. A window share can't
// show it either — the navigator refuses to share its own tab — so it can sit over the canvas. It shows three
// things: the objection just raised and how to handle it, a suggested reply to the question just asked, and the next
// thing to show, one click away ("Show it").
import { h } from "./dom.js";

export const ASSIST_TOPIC = "lax";
const KEEP = 8;              // cards on the panel; older ones drop off the bottom
const QUIET_MS = 50_000;     // the assist says it is alive every 20 s; this long without a word, it has gone
const STORE = "ms-lx";

// Who said a line (CR-20261009-001 L3.1, g-site-speaker): the builder's person on the site camera is "site", labelled
// "At the site" — never folded into the customer. Anything else unknown is hers, as before.
export const speakerOfMsg = (msg) => (["customer", "host", "rm", "site"].includes(msg?.who) ? msg.who : "customer");
export const speakerLabel = (w, names = {}) => (w === "host" ? "You" : w === "rm" ? "RM" : w === "site" ? "At the site" : names.customer || "Customer");

const KIND = { objection: "Objection", reply: "Suggested reply", nudge: "Show next" };

// CR-20261009-001 L3.3 (US-3.3.2): the project's price list is past its date — the card says so before he quotes it.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function staleChip(card) {
  if (!card || card.stale !== true) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(card.priceAsOf || ""));
  const month = m ? MONTHS[Number(m[2]) - 1] : null;
  return month ? `Price list of ${Number(m[3])} ${month} ${m[1]} — confirm before quoting`
    : "Price list may be out of date — confirm before quoting";
}
const STATE = {
  waiting: ["Waiting for live assist to join", "idle"],
  listening: ["Listening", "live"],
  rehearsal: ["Rehearsal", "rehearse"],
  off: ["Not listening", "idle"],
  error: ["Trouble listening", "bad"],
  lost: ["Live assist has dropped", "bad"],
};

function remembered() {
  try { return JSON.parse(localStorage.getItem(STORE) || "{}") || {}; } catch { return {}; }
}
function remember(v) {
  try { localStorage.setItem(STORE, JSON.stringify(v)); } catch { /* a nicety */ }
}

// `names` says who is speaking: { customer, host, rm }. `onShow(action)` takes an action the assist named and moves
// the tour there; `isShowing(action)` says whether the tour is already there.
export function assistPanel({ names, onShow, isShowing }) {
  const saved = remembered();
  const st = { state: "waiting", why: "", cards: [], lines: [], partial: {}, unseen: 0, seenAt: 0, collapsed: saved.collapsed ?? window.innerWidth < 900 };

  const dot = h("i", { class: "lx-dot" });
  const stateEl = h("small", { class: "lx-st" });
  const badge = h("span", { class: "lx-badge", hidden: true });
  const foldBtn = h("button", { class: "lx-fold", type: "button", onclick: () => fold(!st.collapsed) });
  const head = h("header", { class: "lx-h" }, dot, h("b", { text: "Live assist" }), badge, stateEl, h("span", { class: "sp" }), foldBtn);
  const cardsEl = h("div", { class: "lx-cards", "aria-live": "polite" });
  // While the pointer is over the cards, a new one waits behind this bar instead of pushing a button out from under
  // the sales manager's click.
  const held = [];
  let hovering = false;
  const newBar = h("button", { class: "lx-new", type: "button", hidden: true, onclick: () => release() });
  const heardEl = h("div", { class: "lx-heard", "aria-label": "What was just said" });
  const foot = h("p", { class: "lx-foot", text: "Only you see this. Never quote a price, a range or a discount — share the cost sheet." });
  const el = h("aside", { class: "lx", "aria-label": "Live assist — only you see this" }, head, h("div", { class: "lx-b" }, newBar, cardsEl, heardEl, foot));
  cardsEl.addEventListener("pointerenter", () => { hovering = true; });
  cardsEl.addEventListener("pointerleave", () => { hovering = false; release(); });
  if (Number.isFinite(saved.x) && Number.isFinite(saved.y)) place(saved.x, saved.y);

  // ---------------------------------------------------------------------------------------------- messages --
  function receive(msg) {
    if (!msg || typeof msg !== "object") return;
    st.seenAt = Date.now();
    if (msg.t === "status" && STATE[msg.state]) {
      st.state = msg.state;
      st.why = String(msg.why || "").slice(0, 200);
      paintState();
      if (!st.cards.length) paintCards();
    } else if (msg.t === "heard") {
      if (st.state === "waiting" || st.state === "lost") { st.state = "listening"; paintState(); }
      const who = speakerOfMsg(msg);
      const text = String(msg.text || "").slice(0, 400);
      if (msg.final) {
        delete st.partial[who];
        if (text) st.lines.push({ who, text });
        st.lines = st.lines.slice(-3);
      } else st.partial[who] = text;
      paintHeard();
    } else if (msg.t === "card" && KIND[msg.kind]) {
      const card = {
        kind: msg.kind, key: String(msg.key || ""), title: String(msg.title || "").slice(0, 160),
        lines: (Array.isArray(msg.lines) ? msg.lines : []).map((l) => String(l).slice(0, 400)).slice(0, 4),
        heard: msg.heard ? String(msg.heard).slice(0, 240) : "", action: msg.action && typeof msg.action === "object" ? msg.action : null,
        stale: staleChip(msg),
        shown: false,
      };
      if (st.collapsed) st.unseen++;
      paintBadge();
      if (hovering) {
        const i = held.findIndex((c) => c.key === card.key);
        if (i >= 0) held.splice(i, 1);
        held.push(card);
        newBar.textContent = `${held.length} new ↑`;
        newBar.hidden = false;
        return;
      }
      add(card);
    }
  }

  // ---------------------------------------------------------------------------------------------- painting --
  function paintState() {
    const [label, cls] = STATE[st.state];
    dot.className = `lx-dot ${cls}`;
    stateEl.textContent = label;
    stateEl.title = st.why || label;
    el.classList.toggle("rehearse", st.state === "rehearsal");
  }
  function paintBadge() {
    badge.hidden = !(st.collapsed && st.unseen);
    badge.textContent = String(st.unseen);
  }
  function add(...cards) {
    for (const card of cards) st.cards = [card, ...st.cards.filter((c) => c.key !== card.key)];
    st.cards = st.cards.slice(0, KEEP);
    paintCards();
  }
  function release() {
    if (!held.length) return;
    add(...held.splice(0));
    newBar.hidden = true;
  }
  // Each card is built once and then only updated, so a repaint never replaces the button being clicked.
  function paintCards() {
    for (const c of st.cards) { if (!c.el) cardEl(c); paintShow(c); }
    const want = st.cards.length ? st.cards.map((c) => c.el) : [h("p", { class: "lx-empty", text: st.state === "waiting"
      ? "When it joins, it listens to the conversation and puts help here: replies, objections, what to show next."
      : "Help appears here as the customer talks: a reply to their question, how to handle a worry, what to show next." })];
    if (want.length !== cardsEl.children.length || want.some((n, i) => cardsEl.children[i] !== n)) cardsEl.replaceChildren(...want);
  }
  function paintShow(c) {
    if (!c.showBtn) return;
    const showing = isShowing(c.action);
    c.showBtn.disabled = showing;
    c.showBtn.textContent = showing ? (c.shown ? "✓ Shown" : "On screen") : "Show it";
  }
  function cardEl(c) {
    const actions = [];
    if (c.action) {
      c.showBtn = h("button", { class: "lx-show", type: "button", onclick: () => { c.shown = true; onShow(c.action); paintShow(c); } });
      actions.push(c.showBtn);
    }
    actions.push(h("button", { class: "lx-x", type: "button", "aria-label": "Dismiss", text: "×", onclick: () => { st.cards = st.cards.filter((x) => x !== c); paintCards(); } }));
    c.el = h("div", { class: `lx-card k-${c.kind} fresh` },
      h("div", { class: "lx-k", text: KIND[c.kind] }),
      c.stale ? h("span", { class: "lx-stale", text: c.stale }) : null,
      c.kind === "reply" ? null : h("b", { text: c.title }),
      c.lines.length ? (c.kind === "objection" ? h("ul", {}, c.lines.map((l) => h("li", { text: l }))) : h("p", { text: c.lines.join(" ") })) : null,
      c.heard ? h("q", { text: c.heard }) : null,
      h("div", { class: "lx-row" }, actions));
    setTimeout(() => c.el.classList.remove("fresh"), 1600);
  }
  function paintHeard() {
    const who = (w) => speakerLabel(w, names());
    const rows = st.lines.map((l) => h("div", {}, h("b", { text: `${who(l.who)} ` }), l.text));
    for (const [w, text] of Object.entries(st.partial)) if (text) rows.push(h("div", { class: "part" }, h("b", { text: `${who(w)} ` }), text));
    heardEl.replaceChildren(...rows);
    heardEl.hidden = !rows.length;
  }
  // The navigator calls this when the tour moves, so "Show it" knows what is already on screen.
  function refresh() { paintCards(); }

  // ------------------------------------------------------------------------------------ folding and moving --
  function fold(collapsed) {
    st.collapsed = collapsed;
    if (!collapsed) st.unseen = 0;
    el.classList.toggle("folded", collapsed);
    foldBtn.textContent = collapsed ? "Open" : "–";
    foldBtn.setAttribute("aria-label", collapsed ? "Open live assist" : "Fold live assist");
    foldBtn.setAttribute("aria-expanded", String(!collapsed));
    paintBadge();
    remember({ ...remembered(), collapsed });
  }
  function place(x, y) {
    const w = el.offsetWidth || 300, hgt = el.offsetHeight || 60;
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, x));
    const top = Math.max(8, Math.min(window.innerHeight - Math.min(hgt, 60) - 8, y));
    Object.assign(el.style, { left: `${left}px`, top: `${top}px`, right: "auto" });
    return { x: left, y: top };
  }
  let drag = null;
  head.addEventListener("pointerdown", (e) => {
    if (e.target.closest("button") || e.button !== 0) return;
    const r = el.getBoundingClientRect();
    drag = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    head.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  });
  head.addEventListener("pointermove", (e) => { if (drag) place(e.clientX - drag.dx, e.clientY - drag.dy); });
  head.addEventListener("pointerup", () => {
    if (!drag) return;
    drag = null;
    const r = el.getBoundingClientRect();
    remember({ ...remembered(), x: Math.round(r.left), y: Math.round(r.top) });
  });

  const watch = setInterval(() => {
    if (["listening", "rehearsal", "off", "error"].includes(st.state) && Date.now() - st.seenAt > QUIET_MS) { st.state = "lost"; st.why = "No word from it for a minute."; paintState(); }
  }, 5000);

  fold(st.collapsed);
  paintState();
  paintCards();
  paintHeard();
  return { el, receive, refresh, destroy: () => { clearInterval(watch); el.remove(); } };
}
